/**
 * Single source of truth for subscription pricing.
 *
 * To change prices edit ONLY `USD_UNIT_PRICES` (and `INR_PER_USD` to re-peg
 * INR). INR prices are always derived from USD, so the two can never drift.
 * Razorpay plan IDs come from env (`REACT_APP_RAZORPAY_[IN_]PLAN_<TIER>_<CYCLE>`)
 * and must point at Razorpay plans whose amount equals the price below.
 */

export const CURRENCIES = Object.freeze({ USD: 'USD', INR: 'INR' });

export const CURRENCY_SYMBOLS = Object.freeze({
  [CURRENCIES.USD]: '$',
  [CURRENCIES.INR]: '₹',
});

export const PLAN_TIERS = Object.freeze({
  STARTER: 'starter',
  GROW: 'grow',
  ENTERPRISE: 'enterprise',
});

/** Tiers that have a fixed, self-serve price (Enterprise is contact-sales). */
export const PRICED_TIERS = Object.freeze([
  PLAN_TIERS.STARTER,
  PLAN_TIERS.GROW,
]);

export const BILLING_CYCLES = Object.freeze({
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
});

/**
 * Per-cycle presentation + Razorpay metadata.
 * `planPeriod` is the singular form stored on plan records ("/month" etc.).
 */
export const BILLING_CYCLE_CONFIG = Object.freeze({
  [BILLING_CYCLES.MONTHLY]: {
    label: 'Monthly',
    sublabel: 'Billed monthly',
    planPeriod: 'month',
    discountPercent: 0,
  },
  [BILLING_CYCLES.QUARTERLY]: {
    label: 'Quarterly',
    sublabel: 'Billed every 3 months',
    planPeriod: 'quarter',
    discountPercent: 15,
  },
  [BILLING_CYCLES.YEARLY]: {
    label: 'Yearly',
    sublabel: 'Billed yearly',
    planPeriod: 'year',
    discountPercent: 30,
  },
});

/** Fixed conversion used to price INR plans from USD. */
export const INR_PER_USD = 96;

/** USD price per user for one full billing period. The only price input. */
const USD_UNIT_PRICES = Object.freeze({
  [PLAN_TIERS.STARTER]: {
    [BILLING_CYCLES.MONTHLY]: 7,
    [BILLING_CYCLES.QUARTERLY]: 18,
    [BILLING_CYCLES.YEARLY]: 60,
  },
  [PLAN_TIERS.GROW]: {
    [BILLING_CYCLES.MONTHLY]: 10,
    [BILLING_CYCLES.QUARTERLY]: 27,
    [BILLING_CYCLES.YEARLY]: 96,
  },
});

export const convertUsdToInr = (usdAmount, rate = INR_PER_USD) =>
  Math.round(usdAmount * rate);

const buildCurrencyPrices = (usdByCycle, currency) =>
  Object.fromEntries(
    Object.entries(usdByCycle).map(([cycle, usd]) => [
      cycle,
      currency === CURRENCIES.INR ? convertUsdToInr(usd) : usd,
    ])
  );

/** `TIER_PRICING[tier][currency][cycle]` → unit price for that period. */
export const TIER_PRICING = Object.freeze(
  Object.fromEntries(
    PRICED_TIERS.map((tier) => [
      tier,
      Object.freeze(
        Object.fromEntries(
          Object.values(CURRENCIES).map((currency) => [
            currency,
            Object.freeze(buildCurrencyPrices(USD_UNIT_PRICES[tier], currency)),
          ])
        )
      ),
    ])
  )
);

export const getUnitPrice = (tier, cycle, currency) =>
  TIER_PRICING[tier]?.[currency]?.[cycle] ?? null;

// ---------------------------------------------------------------------------
// Razorpay plan IDs (env-driven)
// ---------------------------------------------------------------------------

const planIdEnvName = (tier, cycle, currency) =>
  `REACT_APP_RAZORPAY_${currency === CURRENCIES.INR ? 'IN_' : ''}PLAN_${tier.toUpperCase()}_${cycle.toUpperCase()}`;

/** Razorpay plan ID configured for tier/cycle/currency, or null if unset. */
export const getPlanId = (tier, cycle, currency) =>
  process.env[planIdEnvName(tier, cycle, currency)] || null;

/** `getPlanIdsForCurrency(currency)[tier][cycle]` → plan ID or null. */
export const getPlanIdsForCurrency = (currency) =>
  Object.fromEntries(
    PRICED_TIERS.map((tier) => [
      tier,
      Object.fromEntries(
        Object.values(BILLING_CYCLES).map((cycle) => [
          cycle,
          getPlanId(tier, cycle, currency),
        ])
      ),
    ])
  );

// ---------------------------------------------------------------------------
// Plan catalogue: planId → plan record, built from the plan IDs set in env
// (used to resolve a subscription's plan)
// ---------------------------------------------------------------------------

const TIER_LABELS = Object.freeze({
  [PLAN_TIERS.STARTER]: 'Starter',
  [PLAN_TIERS.GROW]: 'Grow',
});

const buildPlanRecord = ({ id, tier, cycle, currency, price }) => {
  const { planPeriod } = BILLING_CYCLE_CONFIG[cycle];
  const capitalizedPeriod =
    planPeriod.charAt(0).toUpperCase() + planPeriod.slice(1);
  return {
    id,
    name: `${TIER_LABELS[tier]}_${capitalizedPeriod}${currency === CURRENCIES.INR ? '_INR' : ''}`,
    label: TIER_LABELS[tier],
    key: tier,
    billingCycle: planPeriod,
    price,
    currency,
    currencySymbol: CURRENCY_SYMBOLS[currency],
  };
};

const buildPlanCatalogue = () => {
  const catalogue = {};

  // Plans currently configured in env: price comes from the derived pricing.
  PRICED_TIERS.forEach((tier) => {
    Object.values(BILLING_CYCLES).forEach((cycle) => {
      Object.values(CURRENCIES).forEach((currency) => {
        const id = getPlanId(tier, cycle, currency);
        if (!id) return;
        catalogue[id] = buildPlanRecord({
          id,
          tier,
          cycle,
          currency,
          price: getUnitPrice(tier, cycle, currency),
        });
      });
    });
  });

  return Object.freeze(catalogue);
};

/** planId → { id, name, label, key, billingCycle, price, currency, currencySymbol } */
export const plans = buildPlanCatalogue();
